import type { PageContext } from '../../lib/types';
// Admin: Architecture — two diagrams, not a tree. Fixed hand-placed boxes (few enough that an
// auto-layout would be overkill), reusing the same box+connector language as the flow canvas.
import { html } from '../../lib/dom';
import { pageHead, tabs } from '../../components/ui';
import { diagramBox, diagramCanvas, diagramLegend, edgeH, edgeV } from '../../components/diagram';

// ── Information Architecture (IA brief §2, §56) ───────────────────────────
// Solid = the one real hierarchy (Workspace → Project → Flow → Node).
// Dashed = everything else — relationships, not nesting.
function iaDiagram() {
  const W = 170, H = 72;
  const b: any = {
    org: { x: 30, y: 190, w: W, h: H, title: 'Organization', sub: 'Top security & billing boundary', tone: 'neutral' },
    ws: { x: 250, y: 190, w: W, h: H, title: 'Workspaces', sub: 'Rooms containing Projects', tone: 'input' },
    proj: { x: 470, y: 190, w: W, h: H, title: 'Projects', sub: 'Central working unit', tone: 'input' },
    flow: { x: 690, y: 190, w: W, h: H, title: 'Flows', sub: 'Executable graphs', tone: 'input' },
    node: { x: 910, y: 190, w: W, h: H, title: 'Nodes', sub: 'Minimum executable unit', tone: 'input' },
    brand: { x: 250, y: 40, w: W, h: H, title: 'Brands', sub: 'Global, cross-workspace context', tone: 'tool' },
    lib: { x: 690, y: 340, w: W, h: H, title: 'Assets', sub: 'One global resource registry', tone: 'model' },
    runs: { x: 910, y: 40, w: W, h: H, title: 'Runs', sub: 'Global execution history', tone: 'output' },
  };
  const boxes = Object.values(b).map((x?: any) => diagramBox(x));
  const edges = [
    edgeH(b.org, b.ws), edgeH(b.ws, b.proj), edgeH(b.proj, b.flow), edgeH(b.flow, b.node),
    edgeV(b.brand, b.ws, { style: 'dashed' }), edgeV(b.brand, b.proj, { style: 'dashed' }),
    edgeV(b.proj, b.lib, { style: 'dashed' }), edgeV(b.flow, b.lib, { style: 'dashed' }),
    edgeV(b.runs, b.flow, { style: 'dashed' }), edgeV(b.runs, b.node, { style: 'dashed' }),
  ].join('');
  return {
    canvas: diagramCanvas(boxes, edges, { width: 1110, height: 450, label: 'Information architecture' }),
    legend: [{ style: 'solid', label: 'Hierarchy — the one real nesting' }, { style: 'dashed', label: 'Relationship — everything else' }],
  };
}

// ── System Architecture (backend brief §3, §107) ─────────────────────────
function systemDiagram() {
  const W = 160, H = 66;
  const b: any = {
    fe: { x: 20, y: 150, w: W, h: H, title: 'Frontend', sub: 'Next.js', tone: 'neutral' },
    api: { x: 210, y: 150, w: W, h: H, title: 'API / Backend', sub: 'Node.js / TypeScript', tone: 'neutral' },
    core: { x: 410, y: 30, w: W, h: H, title: 'Core', sub: 'Org, Users, Permissions', tone: 'input' },
    editor: { x: 410, y: 150, w: W, h: H, title: 'Editor', sub: 'Flows, Nodes, Edges, Versions', tone: 'model' },
    resources: { x: 410, y: 270, w: W, h: H, title: 'Resources', sub: 'Assets, Connections, Variables', tone: 'tool' },
    pg: { x: 610, y: 150, w: W, h: H, title: 'Postgres', tone: 'neutral' },
    orc: { x: 800, y: 150, w: W, h: H, title: 'Orchestrator', sub: 'Runtime domain · builds DAG', tone: 'output' },
    q: { x: 990, y: 150, w: W, h: H, title: 'Queue', sub: 'Redis + BullMQ', tone: 'output' },
    w1: { x: 1180, y: 60, w: W, h: 54, title: 'Worker', tone: 'output' },
    w2: { x: 1180, y: 150, w: W, h: 54, title: 'Worker', tone: 'output' },
    w3: { x: 1180, y: 240, w: W, h: 54, title: 'Worker', tone: 'output' },
    prov: { x: 1370, y: 150, w: W, h: H, title: 'Providers', sub: 'OpenAI, fal.ai, Google…', tone: 'neutral' },
    events: { x: 800, y: 340, w: W, h: H, title: 'Events', sub: 'run.*, node.* events', tone: 'output' },
    rt: { x: 990, y: 340, w: W, h: H, title: 'Realtime', sub: 'WebSocket → Frontend', tone: 'output' },
  };
  const boxes = Object.values(b).map((x?: any) => diagramBox(x));
  const edges = [
    edgeH(b.fe, b.api), edgeH(b.api, b.core), edgeH(b.api, b.editor), edgeH(b.api, b.resources),
    edgeH(b.core, b.pg), edgeH(b.editor, b.pg), edgeH(b.resources, b.pg),
    edgeH(b.pg, b.orc), edgeH(b.orc, b.q), edgeH(b.q, b.w1), edgeH(b.q, b.w2), edgeH(b.q, b.w3),
    edgeH(b.w1, b.prov), edgeH(b.w2, b.prov), edgeH(b.w3, b.prov),
    edgeV(b.orc, b.events, { style: 'dashed' }), edgeH(b.events, b.rt, { style: 'dashed' }),
  ].join('');
  return {
    canvas: diagramCanvas(boxes, edges, { width: 1560, height: 440, label: 'System architecture' }),
    legend: [{ style: 'solid', label: 'Synchronous request path' }, { style: 'dashed', label: 'Async execution / realtime events' }],
  };
}

export function render(ctx: PageContext) {
  const tab = ctx.query.tab === 'system' ? 'system' : 'ia';
  const d = tab === 'system' ? systemDiagram() : iaDiagram();
  const body = html`<div class="page">
    ${pageHead({ eyebrow: 'Platform admin', title: 'Architecture', lede: tab === 'system'
      ? 'Editor and Runtime are separated on purpose: what you assemble in the canvas is not what actually executes (backend brief §108, principle 2).'
      : 'Not a folder tree. One real hierarchy — the rest are relationships, filtered views of the same records (IA brief §60).' })}
    ${tabs([['ia', 'Information Architecture'], ['system', 'System Architecture']], tab)}
    ${d.canvas}
    ${diagramLegend(d.legend)}
  </div>`;
  return { title: 'Architecture', body };
}
